/**
 * Configuración completa de métricas para un proyecto.
 *
 * A diferencia del ProjectMetricsCard (sidebar del detalle, vista compacta),
 * aquí cabe la tabla extendida: tipo (auto/manual), visibilidad, recurrencia,
 * umbrales editables inline con override, último valor + status, sparkline,
 * y acciones (activar/desactivar).
 *
 * Tres buckets:
 *   - Activas: ya están corriendo para este proyecto.
 *   - Disponibles: opt-in que no se ha activado aún.
 *   - No aplicables: definición tiene applies_to_projects='none' (solo se
 *     muestran como info para que el usuario sepa que existen pero no
 *     pueden vivir en este proyecto sin tocar la definición global).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowLeft, Hand, Power, PowerOff, Sparkles, RotateCcw } from "lucide-react";
import {
  EVAL_VISIBILITY_LABELS,
  EVAL_RECURRENCE_LABELS,
  computeStatus,
  effectiveThresholds,
  type EvalStatus,
  type KpiDefinition,
  type KpiValue,
  type Project,
  type ProjectActivation,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { canSeeDef } from "@/lib/eval-visibility";
import { MetricSparkline } from "@/features/evaluations/MetricSparkline";
import { MetricStatusBadge } from "@/features/evaluations/MetricStatusBadge";
// ProjectAssessmentsConfigSection pausado — ver EVALUATIONS_PAUSED.md.
// import { ProjectAssessmentsConfigSection } from "@/features/projects/ProjectAssessmentsConfigSection";
import { ProjectQuestionnairesConfigSection } from "@/features/projects/ProjectQuestionnairesConfigSection";

// Tab "assessments" pausada — ver EVALUATIONS_PAUSED.md.
type Tab = "kpis" | "questionnaires";

export const Route = createFileRoute("/projects/$projectId/evaluations-config")({
  component: MetricsConfigPage,
});

function MetricsConfigPage() {
  const { projectId } = Route.useParams();
  const [tab, setTab] = useState<Tab>("kpis");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="proyecto"
        title="Configuración del proyecto"
        subtitle="Activa/desactiva por proyecto los KPIs y cuestionarios aplicables."
        actions={
          <Link to="/projects/$projectId" params={{ projectId }} className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Volver al proyecto
          </Link>
        }
      />

      <nav className="card-elevated p-1 flex flex-wrap gap-1">
        <TabBtn current={tab} value="kpis" onClick={() => setTab("kpis")}>
          KPIs
        </TabBtn>
        <TabBtn current={tab} value="questionnaires" onClick={() => setTab("questionnaires")}>
          Cuestionarios
        </TabBtn>
      </nav>

      {tab === "kpis" && <KpisSection />}
      {tab === "questionnaires" && <ProjectQuestionnairesConfigSection projectId={projectId} />}
    </div>
  );
}

function TabBtn({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-sm ${current === value ? "btn-primary" : "btn-ghost"}`}
    >
      {children}
    </button>
  );
}

function KpisSection() {
  const { projectId } = Route.useParams();
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });

  const projectQ = useQuery<{ project: Project }>({
    queryKey: ["project", projectId, "summary"],
    queryFn: () => api.get(`/projects/${projectId}`),
  });

  const defsQ = useQuery<{ definitions: KpiDefinition[] }>({
    queryKey: ["metric-definitions"],
    queryFn: () => api.get("/kpi-definitions?includeInactive=1"),
  });

  const activationsQ = useQuery<{ activations: ProjectActivation[] }>({
    queryKey: ["project", projectId, "activations"],
    queryFn: () => api.get(`/projects/${projectId}/activations`),
  });

  const periods = useMemo(() => buildLastNMonths(6), []);
  const from = periods[0]!;
  const to = periods[periods.length - 1]!;

  const valuesQ = useQuery<{
    values: Array<{ value: KpiValue; definition: KpiDefinition }>;
  }>({
    queryKey: ["metric-values", "project", projectId, from, to],
    queryFn: () => api.get(`/kpi-values?periodFrom=${from}&periodTo=${to}&projectId=${projectId}`),
  });

  const activationByDef = useMemo(() => {
    const m = new Map<string, ProjectActivation>();
    for (const a of activationsQ.data?.activations ?? []) {
      if (a.defKind === "kpi") m.set(a.defId, a);
    }
    return m;
  }, [activationsQ.data]);

  const valuesByDef = useMemo(() => {
    const m = new Map<string, KpiValue[]>();
    for (const row of valuesQ.data?.values ?? []) {
      const list = m.get(row.value.definitionId) ?? [];
      list.push(row.value);
      m.set(row.value.definitionId, list);
    }
    return m;
  }, [valuesQ.data]);

  const buckets = useMemo(() => {
    if (!me.data || !defsQ.data) {
      return { active: [], available: [], not_applicable: [], hidden: [] };
    }
    const all = defsQ.data.definitions.filter((d) => d.level === "project");
    const active: KpiDefinition[] = [];
    const available: KpiDefinition[] = [];
    const not_applicable: KpiDefinition[] = [];
    const hidden: KpiDefinition[] = [];

    for (const d of all) {
      if (!canSeeDef(d.visibility, me.data.role)) {
        hidden.push(d);
        continue;
      }
      const act = activationByDef.get(d.id);
      if (d.appliesToProjects === "all") {
        // Para "all" la activación opcionalmente puede tener active=false (opt-out).
        if (act?.active === false) available.push(d);
        else active.push(d);
      } else if (d.appliesToProjects === "per_project_opt_in") {
        if (act?.active === true) active.push(d);
        else available.push(d);
      } else {
        not_applicable.push(d);
      }
    }
    return { active, available, not_applicable, hidden };
  }, [me.data, defsQ.data, activationByDef]);

  const canEdit = me.data?.role === "admin" || me.data?.role === "lead";
  const isLoading =
    projectQ.isLoading || defsQ.isLoading || activationsQ.isLoading || valuesQ.isLoading;

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-md text-primary" />
        </div>
      )}

      {!isLoading && (
        <>
          <SummaryBar
            active={buckets.active.length}
            available={buckets.available.length}
            auto={buckets.active.filter((d) => !!d.autoSource).length}
            manual={buckets.active.filter((d) => !d.autoSource).length}
          />

          <Bucket
            title="Activas en este proyecto"
            subtitle="Estas métricas se capturan/calculan para el proyecto. Puedes editar sus umbrales o desactivarlas."
            defs={buckets.active}
            state="active"
            activationByDef={activationByDef}
            valuesByDef={valuesByDef}
            periods={periods}
            projectId={projectId}
            canEdit={canEdit}
          />

          <Bucket
            title="Disponibles — activar manualmente"
            subtitle="Métricas opt-in o desactivadas anteriormente. El lead del proyecto las puede encender."
            defs={buckets.available}
            state="available"
            activationByDef={activationByDef}
            valuesByDef={valuesByDef}
            periods={periods}
            projectId={projectId}
            canEdit={canEdit}
          />

          {buckets.not_applicable.length > 0 && (
            <Bucket
              title="No aplicables a proyectos"
              subtitle="Definidas con applies_to_projects='none'. Para usarlas aquí, cambia la definición global desde /admin/kpi-definitions."
              defs={buckets.not_applicable}
              state="locked"
              activationByDef={activationByDef}
              valuesByDef={valuesByDef}
              periods={periods}
              projectId={projectId}
              canEdit={false}
            />
          )}
        </>
      )}
    </div>
  );
}

function SummaryBar({
  active,
  available,
  auto,
  manual,
}: {
  active: number;
  available: number;
  auto: number;
  manual: number;
}) {
  return (
    <div className="card-elevated p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Stat label="Activas" value={active} accent="text-success" />
      <Stat label="Disponibles" value={available} accent="text-base-content/65" />
      <Stat
        label="Auto-calculadas"
        value={auto}
        accent="text-secondary"
        icon={<Sparkles className="size-3.5" />}
      />
      <Stat
        label="Manuales"
        value={manual}
        accent="text-base-content"
        icon={<Hand className="size-3.5" />}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  icon,
}: {
  label: string;
  value: number;
  accent: string;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-base-content/55 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-display font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

function Bucket({
  title,
  subtitle,
  defs,
  state,
  activationByDef,
  valuesByDef,
  periods,
  projectId,
  canEdit,
}: {
  title: string;
  subtitle: string;
  defs: KpiDefinition[];
  state: "active" | "available" | "locked";
  activationByDef: Map<string, ProjectActivation>;
  valuesByDef: Map<string, KpiValue[]>;
  periods: string[];
  projectId: string;
  canEdit: boolean;
}) {
  if (defs.length === 0) return null;
  return (
    <section className="space-y-3">
      <header>
        <h2 className="font-display text-xl font-semibold">{title}</h2>
        <p className="text-sm text-base-content/60">{subtitle}</p>
      </header>
      <ul className="space-y-2">
        {defs.map((d) => (
          <MetricRow
            key={d.id}
            def={d}
            activation={activationByDef.get(d.id)}
            values={valuesByDef.get(d.id) ?? []}
            periods={periods}
            projectId={projectId}
            state={state}
            canEdit={canEdit}
          />
        ))}
      </ul>
    </section>
  );
}

function MetricRow({
  def,
  activation,
  values,
  periods,
  projectId,
  state,
  canEdit,
}: {
  def: KpiDefinition;
  activation: ProjectActivation | undefined;
  values: KpiValue[];
  periods: string[];
  projectId: string;
  state: "active" | "available" | "locked";
  canEdit: boolean;
}) {
  const current = periods[periods.length - 1]!;
  const previous = periods[periods.length - 2]!;
  const curVal = values.find((r) => r.period === current)?.value ?? null;
  const prevVal = values.find((r) => r.period === previous)?.value ?? null;
  const points = periods.map((p) => ({
    period: p,
    value: values.find((r) => r.period === p)?.value ?? null,
  }));
  const status = state === "active" ? computeStatus(curVal, def, activation) : "unknown";
  const eff = effectiveThresholds(def, activation);
  const isAuto = !!def.autoSource;
  const lastValAutoFilled = values.find((r) => r.period === current)?.autoFilled;

  return (
    <li
      className={`card-elevated p-4 ${
        state === "locked" ? "opacity-60" : ""
      } ${statusBorderClass(status)}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-base-content">{def.name}</span>
            {isAuto ? (
              <span className="badge badge-success badge-sm gap-1">
                <Sparkles className="size-3" /> Auto
              </span>
            ) : (
              <span className="badge badge-ghost badge-sm gap-1">
                <Hand className="size-3" /> Manual
              </span>
            )}
            <span className="badge badge-ghost badge-sm">
              {EVAL_VISIBILITY_LABELS[def.visibility]}
            </span>
            <span className="badge badge-ghost badge-sm">
              {EVAL_RECURRENCE_LABELS[def.recurrence]}
            </span>
            {hasOverride(activation) && <span className="badge badge-info badge-sm">override</span>}
          </div>
          {def.description && (
            <p className="text-xs text-base-content/55 mt-1">{def.description}</p>
          )}
          {isAuto && (
            <p className="text-xs text-base-content/50 mt-1 font-mono">source: {def.autoSource}</p>
          )}
        </div>

        {state === "active" && (
          <div className="flex items-center gap-3 shrink-0">
            <MetricSparkline points={points} status={status} width={80} height={26} />
            <div className="flex flex-col items-end gap-0.5">
              <MetricStatusBadge def={def} current={curVal} previous={prevVal} />
              {lastValAutoFilled && (
                <span className="text-[10px] text-success/80 inline-flex items-center gap-0.5">
                  <Sparkles className="size-2.5" /> autorrelleno
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {state === "active" && canEdit && (
        <ThresholdsRow def={def} activation={activation} projectId={projectId} eff={eff} />
      )}

      <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-base-300/40">
        <ActionButtons
          def={def}
          activation={activation}
          projectId={projectId}
          state={state}
          canEdit={canEdit}
        />
      </div>
    </li>
  );
}

function ThresholdsRow({
  def,
  activation,
  projectId,
  eff,
}: {
  def: KpiDefinition;
  activation: ProjectActivation | undefined;
  projectId: string;
  eff: {
    excellent: number | null;
    good: number | null;
    improvable: number | null;
    bad: number | null;
  };
}) {
  const qc = useQueryClient();
  const [excellent, setExcellent] = useState(strOrEmpty(activation?.thresholdExcellentOverride));
  const [good, setGood] = useState(strOrEmpty(activation?.thresholdGoodOverride));
  const [improvable, setImprovable] = useState(strOrEmpty(activation?.thresholdImprovableOverride));
  const [bad, setBad] = useState(strOrEmpty(activation?.thresholdBadOverride));

  type Vals = { excellent: string; good: string; improvable: string; bad: string };
  const save = useMutation({
    mutationFn: (vals: Vals) =>
      api.put(`/projects/${projectId}/activations`, {
        defKind: "kpi",
        defId: def.id,
        active: activation?.active ?? true,
        thresholdExcellentOverride: vals.excellent === "" ? null : Number(vals.excellent),
        thresholdGoodOverride: vals.good === "" ? null : Number(vals.good),
        thresholdImprovableOverride: vals.improvable === "" ? null : Number(vals.improvable),
        thresholdBadOverride: vals.bad === "" ? null : Number(vals.bad),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId, "activations"] }),
  });

  const dirty =
    excellent !== strOrEmpty(activation?.thresholdExcellentOverride) ||
    good !== strOrEmpty(activation?.thresholdGoodOverride) ||
    improvable !== strOrEmpty(activation?.thresholdImprovableOverride) ||
    bad !== strOrEmpty(activation?.thresholdBadOverride);

  const reset = () => {
    setExcellent("");
    setGood("");
    setImprovable("");
    setBad("");
    save.mutate({ excellent: "", good: "", improvable: "", bad: "" });
  };

  return (
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
      <ThresholdInput
        label="Excelente"
        color="info"
        global={def.thresholdExcellent}
        effective={eff.excellent}
        value={excellent}
        onChange={setExcellent}
      />
      <ThresholdInput
        label="Bien"
        color="success"
        global={def.thresholdGood}
        effective={eff.good}
        value={good}
        onChange={setGood}
      />
      <ThresholdInput
        label="Mejorable"
        color="warning"
        global={def.thresholdImprovable}
        effective={eff.improvable}
        value={improvable}
        onChange={setImprovable}
      />
      <ThresholdInput
        label="Malo"
        color="error"
        global={def.thresholdBad}
        effective={eff.bad}
        value={bad}
        onChange={setBad}
      />
      <div className="col-span-2 sm:col-span-4 flex justify-end gap-2">
        {hasOverride(activation) && (
          <button
            type="button"
            onClick={reset}
            disabled={save.isPending}
            className="btn btn-ghost btn-xs gap-1"
            title="Quitar overrides — usar umbrales globales"
          >
            <RotateCcw className="size-3" /> Usar globales
          </button>
        )}
        <button
          type="button"
          onClick={() => save.mutate({ excellent, good, improvable, bad })}
          disabled={!dirty || save.isPending}
          className="btn btn-primary btn-xs"
        >
          {save.isPending && <span className="loading loading-spinner loading-xs" />}
          Guardar umbrales
        </button>
      </div>
    </div>
  );
}

function ThresholdInput({
  label,
  color,
  global,
  effective,
  value,
  onChange,
}: {
  label: string;
  color: "info" | "success" | "warning" | "error";
  global: number | null;
  effective: number | null;
  value: string;
  onChange: (v: string) => void;
}) {
  const override = value !== "";
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className={`text-xs font-medium text-${color}`}>{label}</label>
        <span className="text-[10px] text-base-content/45">
          global: <span className="font-mono">{global ?? "—"}</span>
          {override && effective !== global && (
            <span className="ml-1 text-info">→ {effective}</span>
          )}
        </span>
      </div>
      <input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input input-bordered input-xs w-full mt-0.5"
        placeholder={global == null ? "—" : `usa ${global}`}
      />
    </div>
  );
}

function ActionButtons({
  def,
  activation,
  projectId,
  state,
  canEdit,
}: {
  def: KpiDefinition;
  activation: ProjectActivation | undefined;
  projectId: string;
  state: "active" | "available" | "locked";
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const upsert = useMutation({
    mutationFn: (active: boolean) =>
      api.put(`/projects/${projectId}/activations`, {
        defKind: "metric",
        defId: def.id,
        active,
        thresholdGoodOverride: activation?.thresholdGoodOverride ?? null,
        thresholdImprovableOverride: activation?.thresholdImprovableOverride ?? null,
        thresholdBadOverride: activation?.thresholdBadOverride ?? null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId, "activations"] }),
  });

  if (state === "locked")
    return <span className="text-xs text-base-content/45">Sin acción disponible</span>;

  if (!canEdit) return <span className="text-xs text-base-content/45">Solo lectura</span>;

  if (state === "active") {
    return (
      <button
        type="button"
        onClick={() => {
          if (confirm(`Desactivar "${def.name}" en este proyecto?`)) upsert.mutate(false);
        }}
        disabled={upsert.isPending}
        className="btn btn-ghost btn-xs gap-1"
      >
        <PowerOff className="size-3.5" /> Desactivar
      </button>
    );
  }

  // available
  return (
    <button
      type="button"
      onClick={() => upsert.mutate(true)}
      disabled={upsert.isPending}
      className="btn btn-primary btn-xs gap-1"
    >
      {upsert.isPending && <span className="loading loading-spinner loading-xs" />}
      <Power className="size-3.5" /> Activar
    </button>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function buildLastNMonths(n: number): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getUTCFullYear(), now.getUTCMonth() - i, 1);
    months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return months;
}

function statusBorderClass(s: EvalStatus): string {
  if (s === "excellent") return "border-l-4 border-l-info";
  if (s === "good") return "border-l-4 border-l-success";
  if (s === "improvable") return "border-l-4 border-l-warning";
  if (s === "bad") return "border-l-4 border-l-error";
  return "";
}

function hasOverride(a: ProjectActivation | undefined): boolean {
  if (!a) return false;
  return (
    a.thresholdGoodOverride != null ||
    a.thresholdImprovableOverride != null ||
    a.thresholdBadOverride != null
  );
}

function strOrEmpty(n: number | null | undefined): string {
  return n == null ? "" : String(n);
}
